// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { IconWrapper } from "@insite/mobius/Icon";
import Mail from "@insite/mobius/Icons/Mail";
import Inbox from "@insite/mobius/Icons/Mail";
import MapPin from "@insite/mobius/Icons/MapPin";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ContactTypography from "@keller/App/Components/Common/ContactTypography";
import { styledBoxStyle } from "@keller/App/Components/Common/StyledBox";
import Phone from "@keller/XDL/Icons/Phone";
import { xMask, xValidation } from "@keller/XDL/Utilities";
import { iconLinkCommonStyles } from "@keller/XDL/Utilities/links";
import { mergeWithCss } from "@keller/XDL/Utilities/mergeWithCss";
import { FC } from "react";
import React from "react";
import { css } from "styled-components";

const enum fields {
    phoneNumber = "phoneNumber",
    emailAddress = "emailAddress",
    emailLabel= "emailLabel",
    branchLabel = "branchLabel",
    chatWithUsLink = "chatWithUsLink",
    chatLabel = "chatLabel",
}

interface OwnProps {
    fields: {
        [fields.phoneNumber]: string;
        [fields.emailAddress]: string;
        [fields.emailLabel]: string;
        [fields.branchLabel]: string;
        [fields.chatWithUsLink]: LinkFieldValue;
        [fields.chatLabel]: string;
    };
}

const linkStyle = {
    typographyProps: {
        size: 15,
        weight: 600,
        css: css`
            letter-spacing: 0;
            line-height: 24px;
        `,
    },
    css: css`
        margin-bottom: 12px;
        font-weight: 600;
        & ${IconWrapper} {
            margin-right: 9px;
        }
    `,
};

type Props = OwnProps & WidgetProps;

interface UserSupportStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    subtitle?: TypographyPresentationProps;
    findClosestBranchLink?: LinkPresentationProps;
    emailAddress?: LinkPresentationProps;
    phoneNumber?: LinkPresentationProps;
    chatWithUsLink?: LinkPresentationProps;
}

const UserSupportStyles: UserSupportStyles = {
    wrapper: {
        css: css`
            ${styledBoxStyle};
        `,
    },
    title: {
        variant: "h4",
        size: 20,
        weight: "bold",
        ellipsis: true,
        css: css`
            line-height: 25px;
            display: block;
            letter-spacing: 0;
        `,
    },
    subtitle: {
        size: 15,
        weight: "normal",
        ellipsis: true,
        css: css`
            letter-spacing: 0;
            line-height: 24px;
            display: block;
            white-space: break-spaces;
            margin-bottom: 16px;
        `,
    },
    findClosestBranchLink: {
        ...mergeWithCss(iconLinkCommonStyles(MapPin), linkStyle),
    },
    emailAddress: {
        ...mergeWithCss(iconLinkCommonStyles(Mail), linkStyle),
    },
    phoneNumber: {
        ...mergeWithCss(iconLinkCommonStyles(Phone), linkStyle),
    },
    chatWithUsLink: {
        ...mergeWithCss(iconLinkCommonStyles(Inbox), linkStyle),
    },
};

const styles = UserSupportStyles;

const UserSupport: FC<Props> = ({ fields }) => {
    const history = useHistory();

    const onClick = (url?: string) => url && history.push(url);
    const locationFinderPageUrl = useAppSelector(state => getPageLinkByPageType(state , "LocationFinderPage")?.url);

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="UserSupport">
            <Typography as="h4" {...styles.title}>
                {translate("Have Questions?")}
            </Typography>
            <Typography as="p" {...styles.subtitle}>
                {siteMessage("Keller_UserSupport_Help")}
            </Typography>
            <ContactTypography {...styles.phoneNumber} type="phone" targetValue={fields.phoneNumber} icon={true}>
                {xMask.phoneNumber(fields.phoneNumber)}
            </ContactTypography>
            <ContactTypography {...styles.emailAddress} type="email" targetValue={fields.emailAddress} icon={true}>
                {fields.emailLabel || fields.email}
            </ContactTypography>
            <StyledWrapper>
                <Link href={locationFinderPageUrl} {...styles.findClosestBranchLink}>
                    {fields.branchLabel}
                </Link>
            </StyledWrapper>
            <StyledWrapper>
                {fields.chatWithUsLink.value && fields.chatLabel && (                                   
                    <Link href={fields.chatWithUsLink.value} {...styles.chatWithUsLink}>
                        {fields.chatLabel}
                    </Link>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: UserSupport,
    definition: {
        group: "Create Account",
        allowedContexts: ["CreateAccountPage"],
        fieldDefinitions: [            
            {
                name: fields.phoneNumber,
                displayName: "Phone Number",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                validate: value => {
                    if (!value) {
                        return null;
                    }
                    return xValidation.phone(value)
                        ? null
                        : siteMessage("Field_Invalid", translate("Phone Number")).toString();
                },
            },
            {
                name: fields.emailAddress,
                editorTemplate: "TextField",
                displayName: "Email Address",
                fieldType: "General",
                defaultValue: "",
            },
            {
                name: fields.emailLabel,
                editorTemplate: "TextField",
                defaultValue: "Email Us",
                fieldType: "General",
                displayName: "Email Address Text",
            },
            {
                name: fields.branchLabel,
                editorTemplate: "TextField",
                defaultValue: "Find Your Closest Branch",
                fieldType: "General",
                displayName: "Find Branch Label",
            },
            {
                name: fields.chatWithUsLink,
                displayName: "Chat with us Link destination",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
            },
            {
                name: fields.chatLabel,
                editorTemplate: "TextField",
                defaultValue: "Chat With Us",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
